// CSV column name constants - SHOPLINE CSV export format column definitions
const HEADER_IDENTIFIER = 'spuid,skuid,Handle,';

// Import CSV detector
import { detectCSVFormat } from './csvDetector.js';

/**
 * Clean string value
 * @param {string} val - Raw value
 * @returns {string} - Cleaned value
 */
export function cleanValue(val) {
  return val ? val.trim() : '';
}

/**
 * Custom CSV row parser, handles quoted fields with commas
 * @param {string} row - CSV row string
 * @returns {string[]} - Parsed field array
 */
export function parseCSVRow(row) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    const nextChar = i + 1 < row.length ? row[i + 1] : '';

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Double quote inside quoted field - treat as single quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field end
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  // Add last field
  result.push(current);

  return result;
}

/**
 * Enhanced CSV parser, handles multi-line quoted fields and multiple platform formats
 * @param {string} csv - CSV file content
 * @returns {Object} - Object containing parsedData, headers, and csvFormat
 */
export function parseComplexCSV(csv) {
  // Detect CSV format
  const csvFormat = detectCSVFormat(csv);

  // Parse based on detected format
  if (csvFormat === 'shopify') {
    return parseShopifyCSV(csv);
  }

  // Default to SHOPLINE format
  return parseShoplineCSV(csv);
}

/**
 * Parse SHOPLINE CSV format
 * @param {string} csv - CSV file content
 * @returns {Object} - Object containing parsedData, headers, and csvFormat
 */
function parseShoplineCSV(csv) {
  const lines = csv.split(/\r?\n/);

  // Find actual header start position (SHOPLINE CSV has multiple description lines before header)
  const headerIndex = lines.findIndex(line => line.startsWith(HEADER_IDENTIFIER));

  if (headerIndex === -1) {
    throw new Error('Could not find header row in CSV file');
  }

  // Parse header line
  const headers = parseCSVRow(lines[headerIndex]);

  // Collect complete multi-line records
  const records = collectCompleteRecords(lines, headerIndex + 1, headers.length);

  // Parse records into objects
  const parsedData = records
    .filter(record => record.trim())
    .map(record => parseRecordToObject(record, headers));

  return { csvFormat: 'shopline', headers, parsedData };
}

/**
 * Parse Shopify CSV format
 * @param {string} csv - CSV file content
 * @returns {Object} - Object containing parsedData, headers, and csvFormat
 */
function parseShopifyCSV(csv) {
  const lines = csv.split(/\r?\n/);

  // Find header line (Shopify has single line header)
  const headerIndex = lines.findIndex(line => {
    const trimmed = line.trim();
    return trimmed.startsWith('Handle,Title,') ||
           trimmed.startsWith('Handle,Title,');
  });

  if (headerIndex === -1) {
    // Try alternative detection
    const altHeaderIndex = lines.findIndex(line =>
      line.includes('Handle') &&
      line.includes('Title') &&
      (line.includes('Variant SKU') || line.includes('Variant Price'))
    );

    if (altHeaderIndex === -1) {
      throw new Error('Could not find header row in Shopify CSV file');
    }
  }

  const actualHeaderIndex = headerIndex !== -1 ? headerIndex :
    lines.findIndex(line =>
      line.includes('Handle') &&
      line.includes('Title') &&
      (line.includes('Variant SKU') || line.includes('Variant Price'))
    );

  // Parse header line
  const headers = parseCSVRow(lines[actualHeaderIndex]);

  // For Shopify, we need to handle variant rows differently
  // Each product can have multiple variant rows
  const records = collectCompleteRecords(lines, actualHeaderIndex + 1, headers.length);

  // Parse records and group by product Handle
  const parsedData = parseShopifyRecords(records, headers);

  return { csvFormat: 'shopify', headers, parsedData };
}

/**
 * Parse Shopify records and group variants by product
 * @param {string[]} records - CSV record strings
 * @param {string[]} headers - Column headers
 * @returns {Object[]} - Parsed product objects
 */
function parseShopifyRecords(records, headers) {
  const products = new Map();

  for (const record of records) {
    if (!record.trim()) {continue;}

    const row = parseRecordToObject(record, headers);
    if (!row) {continue;}

    const handle = row['Handle'] || '';
    if (!handle) {continue;}

    // If this is a new product (not a variant row)
    if (row['Title'] && !products.has(handle)) {
      products.set(handle, {
        ...row,
        variants: []
      });
    } else if (products.has(handle)) {
      // This is a variant row - add to product's variants
      const product = products.get(handle);
      product.variants.push(row);
    }
  }

  return [...products.values()];
}

/**
 * Collect complete multi-line records from CSV lines
 * @param {string[]} lines - CSV lines
 * @param {number} startIndex - Starting index
 * @param {number} expectedFieldCount - Expected number of fields
 * @returns {string[]} - Array of complete record strings
 */
function collectCompleteRecords(lines, startIndex, expectedFieldCount) {
  const records = [];
  let currentRecord = '';

  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) {continue;}

    currentRecord += (currentRecord ? '\n' : '') + line;

    // Check if a record is complete
    const row = parseCSVRow(currentRecord);
    if (row.length === expectedFieldCount) {
      records.push(currentRecord);
      currentRecord = '';
    }
  }

  // Add remaining content if valid
  if (currentRecord && parseCSVRow(currentRecord).length === expectedFieldCount) {
    records.push(currentRecord);
  }

  return records;
}

/**
 * Parse a CSV record string into an object
 * @param {string} record - CSV record string
 * @param {string[]} headers - Column headers
 * @returns {Object} - Parsed object
 */
function parseRecordToObject(record, headers) {
  const row = parseCSVRow(record);

  if (row.length !== headers.length) {
    return null;
  }

  const obj = {};
  for (let j = 0; j < headers.length; j++) {
    let value = row[j] ? row[j].trim() : '';
    // Remove extra quotes if present
    if (value.startsWith('"') && value.endsWith('"')) {
      value = value.slice(1, -1).replace(/""/g, '"');
    }
    obj[headers[j]] = value;
  }

  return obj;
}
